import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { User, Wallet, History, Heart, RefreshCw, Loader2, CreditCard, Sun, Moon, Sparkles } from "lucide-react";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";

const THEMES = [
  { id: "light", label: "Clair", icon: Sun },
  { id: "dark", label: "Sombre", icon: Moon },
  { id: "premium", label: "Premium", icon: Sparkles },
];

const TABS = [
  { id: "profil", label: "Profil", icon: User },
  { id: "historique", label: "Commandes", icon: History },
  { id: "favoris", label: "Favoris", icon: Heart },
  { id: "portefeuille", label: "Portefeuille", icon: Wallet },
];

const STATUS_LABEL = {
  nouvelle: "Nouvelle", confirmee: "Confirmée", en_preparation: "En préparation",
  prete: "Prête", en_livraison: "En livraison", completee: "Complétée", annulee: "Annulée",
};

export default function Account() {
  const nav = useNavigate();
  const { user, loading, openAuth, updateProfile } = useAuth();
  const { reorder } = useCart();
  const [tab, setTab] = useState("profil");

  useEffect(() => { if (!loading && !user) openAuth(); }, [loading, user, openAuth]);

  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-coral" /></div>;
  if (!user) return (
    <div className="mx-auto max-w-lg px-4 py-24 text-center">
      <h1 className="font-heading text-2xl font-bold text-foreground">Connectez-vous</h1>
      <p className="text-muted-foreground mt-2">Accédez à vos commandes, favoris et informations.</p>
      <button onClick={openAuth} className="mt-6 px-6 py-3 rounded-full bg-primary text-primary-foreground font-heading font-semibold">Se connecter</button>
    </div>
  );

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-foreground">Bonjour, {user.name.split(" ")[0]}</h1>
      <p className="text-muted-foreground mt-1">{user.email} · {user.account_type === "corporatif" ? "Compte corporatif" : "Particulier"}</p>

      <div className="mt-8 grid lg:grid-cols-[220px_1fr] gap-8">
        <aside className="flex lg:flex-col gap-1 overflow-x-auto lg:sticky lg:top-24 self-start">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`account-tab-${t.id}`}
              className={`flex items-center gap-2.5 px-4 py-3 rounded-xl font-heading text-sm font-medium whitespace-nowrap transition-colors ${tab === t.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-foreground/5"}`}>
              <t.icon className="h-4.5 w-4.5" /> {t.label}
            </button>
          ))}
        </aside>

        <div>
          {tab === "profil" && <ProfileTab user={user} updateProfile={updateProfile} />}
          {tab === "historique" && <HistoryTab reorder={reorder} nav={nav} />}
          {tab === "favoris" && <FavoritesTab user={user} />}
          {tab === "portefeuille" && <WalletTab />}
        </div>
      </div>
    </div>
  );
}

function ProfileTab({ user, updateProfile }) {
  const { changeTheme } = useAuth();
  const { theme } = useTheme();
  const [form, setForm] = useState({
    name: user.name, phone: user.phone || "", company: user.company || "",
    address: user.address || "", postal_code: user.postal_code || "", city: user.city || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async (e) => { e.preventDefault(); setBusy(true); try { await updateProfile(form); } finally { setBusy(false); } };

  return (
    <form onSubmit={save} className="rounded-2xl bg-ink-800 border border-border p-6 max-w-2xl">
      <h2 className="font-heading font-bold text-foreground mb-5">Mes informations</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <F label="Nom complet" value={form.name} onChange={set("name")} testid="profile-name" />
        <F label="Téléphone" value={form.phone} onChange={set("phone")} testid="profile-phone" />
        {user.account_type === "corporatif" && <F label="Entreprise" value={form.company} onChange={set("company")} testid="profile-company" />}
        <div className="sm:col-span-2"><F label="Adresse" value={form.address} onChange={set("address")} testid="profile-address" /></div>
        <F label="Code postal" value={form.postal_code} onChange={set("postal_code")} testid="profile-postal" />
        <F label="Ville" value={form.city} onChange={set("city")} testid="profile-city" />
      </div>
      <div className="mt-6">
        <span className="text-sm text-muted-foreground">Thème d'affichage (mémorisé sur votre compte)</span>
        <div className="mt-2 grid grid-cols-3 gap-2 max-w-md">
          {THEMES.map((t) => (
            <button
              key={t.id}
              type="button"
              data-testid={`profile-theme-${t.id}`}
              onClick={() => changeTheme(t.id)}
              className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border text-sm transition-colors ${theme === t.id ? "bg-primary text-primary-foreground border-primary" : "bg-foreground/5 border-border hover:border-foreground/40"}`}
            >
              <t.icon className="h-5 w-5" /> {t.label}
            </button>
          ))}
        </div>
      </div>
      <button disabled={busy} data-testid="profile-save" className="mt-6 px-6 py-3 rounded-xl bg-primary text-primary-foreground hover:opacity-90 font-heading font-semibold transition-colors disabled:opacity-60 flex items-center gap-2">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer
      </button>
    </form>
  );
}

function HistoryTab({ reorder, nav }) {
  const { data: orders = [], isLoading } = useQuery({ queryKey: ["my-orders"], queryFn: async () => (await api.get("/orders")).data });
  if (isLoading) return <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-coral" /></div>;
  if (orders.length === 0) return <Empty text="Aucune commande pour l'instant." />;

  const handleReorder = (o) => { reorder(o.items); nav("/commander"); };

  return (
    <div className="space-y-4">
      {orders.map((o) => (
        <div key={o.id} data-testid={`order-${o.order_number}`} className="rounded-2xl bg-ink-800 border border-border p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="font-heading font-bold text-foreground">n° {o.order_number}</p>
              <p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString("fr-CA")} · {o.fulfillment.mode === "livraison" ? "Livraison" : "Cueillette"}</p>
            </div>
            <span className="px-3 py-1 rounded-full bg-foreground/10 text-foreground text-xs font-semibold">{STATUS_LABEL[o.status] || o.status}</span>
          </div>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {o.items.slice(0, 5).map((i, idx) => <img key={idx} src={i.image} alt="" className="h-11 w-11 rounded-lg object-cover" />)}
            {o.items.length > 5 && <span className="text-xs text-muted-foreground">+{o.items.length - 5}</span>}
          </div>
          <div className="flex items-center justify-between mt-4">
            <span className="font-heading font-bold text-coral">{money(o.total)}</span>
            <button onClick={() => handleReorder(o)} data-testid={`reorder-${o.order_number}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-foreground/5 hover:bg-foreground/10 border border-border text-sm font-heading font-medium transition-colors">
              <RefreshCw className="h-4 w-4 text-coral" /> Commander à nouveau
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function FavoritesTab({ user }) {
  const { data: products = [], isLoading } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/products")).data });
  const favs = products.filter((p) => user.favorites?.includes(p.id));
  if (isLoading) return <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-coral" /></div>;
  if (favs.length === 0) return <Empty text="Aucun favori. Cliquez sur le ❤ d'un produit pour l'ajouter." />;
  return <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">{favs.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>;
}

function WalletTab() {
  return (
    <div className="rounded-2xl bg-ink-800 border border-border p-6 max-w-xl" data-testid="wallet-tab">
      <h2 className="font-heading font-bold text-foreground mb-4 flex items-center gap-2"><Wallet className="h-5 w-5 text-coral" /> Méthodes de paiement</h2>
      <div className="flex items-center gap-3 p-4 rounded-xl bg-muted border border-border">
        <CreditCard className="h-6 w-6 text-coral shrink-0" />
        <div>
          <p className="font-heading font-semibold text-foreground">Paiement en ligne bientôt disponible</p>
          <p className="text-sm text-muted-foreground">La gestion de vos cartes et du portefeuille (EcoPay) arrive prochainement. Pour l'instant, le règlement se fait à la cueillette ou à la livraison.</p>
        </div>
      </div>
    </div>
  );
}

function F({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="text-sm text-muted-foreground">{label}</span>
      <input {...props} data-testid={testid} className="mt-1 w-full px-4 py-2.5 rounded-lg bg-foreground/5 border border-border text-foreground focus:border-coral focus:outline-none transition-colors" />
    </label>
  );
}
function Empty({ text }) { return <div className="py-16 text-center text-muted-foreground" data-testid="account-empty">{text}</div>; }
